/**
 * Exportar uma lista — F131.
 *
 * *"a página de cliente e produto tem que selecionar as colunas e conseguir
 * exportar como JSON, XLSX, CSV"*. A regra desta pasta é uma só: **o arquivo
 * tem as colunas que estão na tela**, na mesma ordem, e as linhas do filtro que
 * está na tela — todas as páginas, e não só a que aparece. Quem escolheu
 * "Código, Nome, Preço" no botão Colunas não quer abrir a planilha e apagar
 * vinte colunas.
 *
 * Nenhum valor passa por número de JavaScript (ADR-0016). Um número sai daqui
 * como o texto decimal exato que a API mandou; o CSV só troca o ponto pela
 * vírgula e o XLSX escreve o texto direto na célula numérica.
 */

/**
 * Uma célula: texto, número (decimal exato, em texto), data (ISO) ou vazia.
 *
 * A data é um tipo à parte porque cada formato quer uma: a planilha e o CSV
 * mostram `13/09/2026` para quem lê, o JSON manda o ISO para quem programa.
 */
export type ExportValue =
  string | { readonly number: string } | { readonly date: string } | null

export type ExportColumn<Row> = {
  /** A chave do JSON. Estável, sem acento: é o que um programa lê. */
  readonly id: string
  /** O cabeçalho do CSV e da planilha: o que a pessoa lê. */
  readonly label: string
  readonly value: (row: Row) => ExportValue
}

export type ExportFormat = 'csv' | 'xlsx' | 'json'

/**
 * Até onde uma exportação vai.
 *
 * O catálogo da Elite tem 5309 produtos: 54 páginas de 100. O teto existe para
 * um cadastro de cem mil linhas não travar o navegador — e quando ele corta, a
 * tela diz, em vez de entregar um arquivo que parece completo.
 */
export const MAX_EXPORT_ROWS = 20_000

/** `189.900000` → `189.9`; `12.000000` → `12`; `-0.500` → `-0.5`. */
export const trimDecimal = (value: string): string => {
  if (!value.includes('.')) return value
  const trimmed = value.replace(/0+$/, '').replace(/\.$/, '')
  return trimmed === '-0' || trimmed === '' ? '0' : trimmed
}

export const numberValue = (value: string | null | undefined): ExportValue =>
  value == null ? null : { number: trimDecimal(value) }

export const textValue = (value: string | null | undefined): ExportValue =>
  value == null || value === '' ? null : value

export const dateValue = (value: string | null | undefined): ExportValue =>
  value == null ? null : { date: value }

const dayFormat = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })

/** O que a pessoa lê: texto e número como vieram, data em `dd/mm/aaaa`. */
export const readableValue = (value: ExportValue): ExportValue =>
  value !== null && typeof value !== 'string' && 'date' in value
    ? dayFormat.format(new Date(value.date))
    : value

/** O texto de uma célula, no formato de máquina. */
const plain = (value: ExportValue): string | null => {
  if (value === null || typeof value === 'string') return value
  return 'number' in value ? value.number : value.date
}

/** O BOM do UTF-8, por código: um literal invisível no arquivo some na revisão. */
const BOM = String.fromCharCode(0xfeff)

const CSV_NEEDS_QUOTES = /[";\r\n]|^\s|\s$/

/**
 * Uma célula de texto que começa com `=`, `+`, `-` ou `@` vira fórmula quando o
 * Excel abre o CSV — um cadastro com o nome `=HYPERLINK(...)` executaria na
 * máquina de quem exportou. O apóstrofo na frente é a defesa recomendada pela
 * OWASP ("CSV Injection"); números não passam por aqui, então `-2` continua
 * sendo menos dois.
 */
const neutralised = (text: string): string =>
  /^[=+\-@\t\r]/.test(text) ? `'${text}` : text

const csvCell = (value: ExportValue): string => {
  const readable = readableValue(value)
  if (readable === null) return ''
  const text =
    typeof readable === 'string'
      ? neutralised(readable)
      : (plain(readable)?.replace('.', ',') ?? '')
  return CSV_NEEDS_QUOTES.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/**
 * CSV do jeito que o Excel brasileiro abre com dois cliques.
 *
 * Ponto e vírgula e vírgula decimal, porque é o que o Excel em português
 * espera; com vírgula como separador, `189,90` partiria em duas colunas. O BOM
 * no começo é o que faz o Excel ler UTF-8 — sem ele, "São Paulo" vira
 * "SÃ£o Paulo".
 */
export const toCsv = <Row>(
  columns: readonly ExportColumn<Row>[],
  rows: readonly Row[]
): string => {
  const lines = [
    columns.map((column) => csvCell(column.label)).join(';'),
    ...rows.map((row) =>
      columns.map((column) => csvCell(column.value(row))).join(';')
    ),
  ]
  return `${BOM}${lines.join('\r\n')}\r\n`
}

/**
 * JSON para programa ler: chave é o id da coluna, número é texto decimal
 * exato (como a API manda), data em ISO, vazio é `null`.
 */
export const toJson = <Row>(
  columns: readonly ExportColumn<Row>[],
  rows: readonly Row[]
): string =>
  `${JSON.stringify(
    rows.map((row) =>
      Object.fromEntries(
        columns.map((column) => [column.id, plain(column.value(row))])
      )
    ),
    null,
    2
  )}\n`

/** As células de cada linha, na ordem das colunas, prontas para a planilha. */
export const toGrid = <Row>(
  columns: readonly ExportColumn<Row>[],
  rows: readonly Row[]
): ExportValue[][] =>
  rows.map((row) => columns.map((column) => readableValue(column.value(row))))

export type CollectedRows<Row> = {
  readonly rows: Row[]
  readonly total: number
  /** Parou no teto antes do fim. */
  readonly truncated: boolean
}

/**
 * Todas as páginas do filtro atual, uma depois da outra.
 *
 * Em sequência e não em paralelo: 54 pedidos de uma vez derrubariam a API da
 * loja no meio do expediente. O tamanho da página é o máximo que o servidor
 * aceita; quem chama não pode passar dele.
 */
export const collectPages = async <Row>(
  fetchPage: (
    page: number
  ) => Promise<{ readonly items: readonly Row[]; readonly total: number }>,
  options: {
    readonly pageSize: number
    readonly limit?: number
    readonly onProgress?: (done: number, total: number) => void
  }
): Promise<CollectedRows<Row>> => {
  const limit = options.limit ?? MAX_EXPORT_ROWS
  const rows: Row[] = []

  for (let page = 1; ; page += 1) {
    const result = await fetchPage(page)
    rows.push(...result.items)
    options.onProgress?.(Math.min(rows.length, result.total), result.total)

    // Uma página vazia encerra também: o total pode ter caído enquanto
    // exportava (alguém arquivou um cadastro), e o laço não pode girar à toa.
    if (
      result.items.length === 0 ||
      rows.length >= result.total ||
      rows.length >= limit ||
      page * options.pageSize >= result.total
    ) {
      return {
        rows: rows.slice(0, limit),
        total: result.total,
        truncated: result.total > limit,
      }
    }
  }
}

/** `produtos-2026-09-13.csv`, com a data de quem exportou. */
export const exportFileName = (
  base: string,
  format: ExportFormat,
  now: Date = new Date()
): string => {
  const pad = (value: number): string => String(value).padStart(2, '0')
  const day = `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  return `${base}-${day}.${format}`
}
